import React, { useEffect, useMemo, useRef, useState } from "react"
import { ListGroup, Overlay, Popover } from "react-bootstrap"
import ScrollListener from "../../ScrollListener";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InputText from "./InputText";

export default function AsyncSelectV2({
    id, name,
    value, 
    onChange,
    onSelect,
    onBlur, onFocus,
    disabled = false,

    format = value => value,
    formatValue,
    formatItem,

    
    service,
    filter, sort, columns, params, limit = 10,
    parseQuery,

    nullable,
    nullLabel = "Remover Seleção",
    noRecordsLabel = "Nenhum Registro Encontrado",
    placeholder = "",
    className,

    ...rest
}) {
    const [showOverlay, setShowOverlay] = useState(false);
    const inputRef = useRef();

    const [list, setList] = useState([]);
    const [count, setCount] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");

    const finalFilter = useMemo(() => {
        let finalFilter = { ...filter };
        if(parseQuery && query) finalFilter = { ...finalFilter, ...parseQuery(query) }
        return finalFilter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filter), query, parseQuery]);

    useEffect(() => {
        setLoading(true);
        const timeout = setTimeout(async () => {
            let res = await service.list({ 
                limit, offset: 0, 
                sort,  columns, params,
                filter: finalFilter
            });
            setList(res.data.list);
            setCount(res.data.count);
            setLoading(false);
        }, 200);
        return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columns), finalFilter, limit, JSON.stringify(params), service, JSON.stringify(sort)]);

    useEffect(() => {
        setList([]);
        setCount(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(columns), JSON.stringify(filter), JSON.stringify(params), service, JSON.stringify(sort)])

    function select(value) {
        if(onChange) onChange({ target: { id, name, value }})
        if(onSelect) onSelect(value);
        setQuery("");
        setShowOverlay(false);
    }

    function handleChange(e) {
        setQuery(e.target.value);
        setList([]);
        setCount(undefined);
    }

    function handleBlur(e) {
        setShowOverlay(false);
        if(onBlur) onBlur(e);
    }

    function handleFocus(e) {
        setShowOverlay(true);
        if(onFocus) onFocus(e);
    }

    function handleClick(e) {
        setShowOverlay(true);
    }

    async function handleScrollBottom() {
        if(!loading && count > list.length) {
            setLoading(true);
            let res = await service.list({ 
                limit, offset: list.length, 
                sort,  columns, params,
                filter: finalFilter
            });
            setList([...list, ...res.data.list]);
            setCount(res.data.count);
            setLoading(false);
        }
    }

    function handleKeyPress(e) {
        if(e.key === "Enter") {
            select(list[0]);
            setShowOverlay(false);
        }
    }

    return (
        <>
            <Overlay show={showOverlay} target={inputRef.current} placement="bottom-start">
                <ScrollListener as={Popover} style={{maxHeight: 200, overflowY: 'auto', minWidth: inputRef.current ? inputRef.current.clientWidth : 0 }}
                    onScrollBottom={handleScrollBottom}>
                    <Popover.Content className="px-0">
                        <ListGroup variant="flush">
                            { nullable && value && (
                                <ListGroup.Item action 
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => select(null)}
                                    className={`text-center text-muted list-group-item-sm`}>
                                    <FontAwesomeIcon icon="times"/> {nullLabel}
                                </ListGroup.Item>
                            )}
                            { !loading && list.length === 0 && (
                                <ListGroup.Item
                                    className={`text-center text-muted list-group-item-sm`}>
                                    {noRecordsLabel}
                                </ListGroup.Item>
                            )}
                            {list.map((item, index) => (
                                <ListGroup.Item key={index} action active={item === value} 
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => select(item)}
                                    className={`list-group-item-sm`}>
                                    {formatItem ? formatItem(item) : format(item)}
                                </ListGroup.Item>
                            ))}
                            {loading && 
                                <ListGroup.Item className={`text-center text-muted list-group-item-sm`}>
                                    <FontAwesomeIcon icon="cog" spin /> Carregando...
                                </ListGroup.Item> 
                            }
                        </ListGroup>
                    </Popover.Content>
                </ScrollListener>
            </Overlay>
            <InputText {...rest} ref={inputRef} value={(showOverlay) ? query : formatValue ? formatValue(value) : format(value)}
                className={[className, "custom-select"].join(" ")}
                placeholder={value ? format(value) : placeholder}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={handleClick}
                onKeyPress={handleKeyPress}
                disabled={disabled}
            />
            
        </>
    )
}

export const SimpleQueryParser = ({ 
    strings = [],
    integers = [],
    longs = [],
    doubles = [],
    ignore = []
}) => {
    return function(query) {
        const tokens = query.split(" ");
        let filter = {};

        for(let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if(!token) continue;
            if(ignore.includes(token)) continue;

            for(let string of strings) filter = {
                ...filter,
                [`${i}:${string}`]: { $ilike: token }
            }

            if(!isNaN(token)) {
                for(let integer of integers) filter = {
                    ...filter,
                    [`${i}:${integer}`]: { $eq: `int:${token}` }
                }
                for(let long of longs) filter = {
                    ...filter,
                    [`${i}:${long}`]: { $eq: `long:${token}` }
                }
                for(let double of doubles) filter = {
                    ...filter,
                    [`${i}:${double}`]: { $eq: `double:${token}` }
                }
            }
        }
        
        return { $or: filter };
    }
}